import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';

import { notify } from '@/actions/ui';
import { OTP_ERROR_CODES } from '@/auth/components/NewCustomerSignupModal/utils/errorHandling';
import { OtpInput } from '@/auth/components/shared';
import {
  SocialProvider,
  sendSocialConnectCode,
  socialProviderName,
  verifySocialConnect,
} from '@/auth/utils/socialSignIn';
import { socialErrorMessage, socialSignInFailure } from '@/auth/utils/socialSignInFailure';
import Button from '@/componentsv2/Button';
import { firstErrorCode } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { useApp } from '@/hooks/useAppContext';
import { useAppDispatch } from '@/store';

interface SocialConnectStepProps {
  provider: SocialProvider;
  /** The existing account's email, which socialSignIn named. */
  email: string;
  proof: 'NONE' | 'EMAIL_CODE';
  /** socialSignIn's connect token. */
  token: string;
  /** Signs in as the connected account; rejects when that fails. */
  onConnected: (username: string) => Promise<void>;
  onCancel: () => void;
}

const CODE_LENGTH = 6;
const ERROR_ID = 'social-connect-error';
const RESEND_COOLDOWN_SECONDS = 30;
const TOO_MANY_ATTEMPTS = 'Too many attempts. Try again later.';
const SEND_FAILED = "We couldn't send the code. Please try again.";

// Refusals no retry with the same token can get past: the step closes and the
// form says why.
const TERMINAL_CODES: ReadonlySet<string> = new Set([
  ERROR_CODES.AUTH_SOCIAL_CONNECT_TOKEN_INVALID,
  ERROR_CODES.AUTH_SOCIAL_IDENTITY_TAKEN,
  ERROR_CODES.AUTH_SOCIAL_PROVIDER_CONNECTED,
  ERROR_CODES.AUTH_SOCIAL_DISABLED,
  ERROR_CODES.AUTH_SOCIAL_NOT_LINKED,
]);

const CODE_MESSAGES: Record<string, string> = {
  [OTP_ERROR_CODES.OTP_INVALID]: 'That code is not right.',
  [OTP_ERROR_CODES.OTP_EXPIRED]: 'That code has expired. Send a new one.',
  [OTP_ERROR_CODES.OTP_MAX_ATTEMPTS_EXCEEDED]: TOO_MANY_ATTEMPTS,
  [OTP_ERROR_CODES.OTP_RATE_LIMIT_EXCEEDED]: TOO_MANY_ATTEMPTS,
};

/** `unknown` is the copy for an error with no known code, which is reported. */
function failureMessage(err: unknown, provider: SocialProvider, unknown?: string): string {
  const code = firstErrorCode(err);
  const known = code ? CODE_MESSAGES[code] : undefined;
  if (known) return known;
  const { message, expected } = socialSignInFailure(err, provider);
  if (expected) return message;
  Sentry.captureException(err);
  return unknown ?? message;
}

/**
 * ENG-6192: "Continue with Google" or "Continue with Microsoft" met an existing
 * account. Asks before connecting the provider account to it: one click when
 * the provider vouches for the email (NONE), a mailed code otherwise.
 */
export function SocialConnectStep({
  provider,
  email,
  proof,
  token,
  onConnected,
  onCancel,
}: SocialConnectStepProps): JSX.Element {
  const { graphql } = useApp();
  const dispatch = useAppDispatch();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // Set once socialConnectVerify has linked the identity: a retry after a
  // failed sign-in only signs in again, since the token or code is spent.
  const [connectedAs, setConnectedAs] = useState<string>();
  const askingForCode = proof === 'EMAIL_CODE' && sent && !connectedAs;

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const tick = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(tick);
  }, [cooldown]);

  function fail(err: unknown, unknown?: string) {
    const errorCode = firstErrorCode(err);
    if (errorCode && TERMINAL_CODES.has(errorCode)) {
      dispatch(notify(socialErrorMessage(errorCode, provider), 'error'));
      onCancel();
      return;
    }
    setError(failureMessage(err, provider, unknown));
    setBusy(false);
  }

  async function handleSend() {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await sendSocialConnectCode(graphql, token);
    } catch (err) {
      fail(err, SEND_FAILED);
      return;
    }
    setSent(true);
    setCode('');
    setCooldown(RESEND_COOLDOWN_SECONDS);
    setBusy(false);
  }

  // On success the page redirects, so the step stays busy.
  async function handleConnect() {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const username =
        connectedAs ??
        (await verifySocialConnect(graphql, token, askingForCode ? code : undefined));
      setConnectedAs(username);
      await onConnected(username);
    } catch (err) {
      setCode('');
      fail(err);
    }
  }

  const name = socialProviderName(provider);

  return (
    // Centred in the login and signup column, titled like the pages' other steps.
    <div className="mx-auto flex w-full max-w-[360px] flex-col gap-16 text-center">
      <h2 className="mt-[0]">{connectedAs ? `${name} connected` : `Connect ${name}`}</h2>
      <p className="m-[0] text-on-surface">
        {connectedAs
          ? `${name} is connected. Finishing sign-in.`
          : `An OnFrontiers account already uses ${email}. Connect ${name} to it?`}
      </p>
      {askingForCode && (
        <>
          <p className="m-[0] text-on-surface">{`Enter the code we sent to ${email}.`}</p>
          <OtpInput
            value={code}
            onChange={setCode}
            length={CODE_LENGTH}
            disabled={busy}
            autoFocusFirstCell
            hasError={!!error}
            aria-describedby={error ? ERROR_ID : undefined}
          />
        </>
      )}
      {error && (
        <p id={ERROR_ID} role="alert" className="m-[0] text-14 text-[var(--errorText)]">
          {error}
        </p>
      )}
      {proof === 'EMAIL_CODE' && !sent ? (
        <Button className="min-h-48" disabled={busy} onClick={handleSend}>
          Send code
        </Button>
      ) : (
        <Button
          className="min-h-48"
          disabled={busy || (askingForCode && code.length !== CODE_LENGTH)}
          onClick={handleConnect}
        >
          {connectedAs ? 'Sign in' : 'Connect'}
        </Button>
      )}
      {askingForCode && (
        <Button
          className="min-h-48"
          variant="secondary"
          disabled={busy || cooldown > 0}
          onClick={handleSend}
        >
          {cooldown > 0 ? `Resend code (${cooldown}s)` : 'Resend code'}
        </Button>
      )}
      <Button className="min-h-48" variant="tertiary" disabled={busy} onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
